import {type CallPut, type SecurityType} from './common';

/** Identifies the side of an option contract selected from an option chain. */
export enum OptionType {
  CALL = 'CALL',
  PUT = 'PUT',
}

/** Describes the option order styles currently supported by Options 2.0 tickets. */
export type OptionOrderType = 'LIMIT' | 'MARKET';

/**
 * Represents a user-selected option leg shared by the option chain, ticket,
 * validation, hydration, and JPM MLS request flows.
 */
export type OptionLeg = {
  id: string;
  type: OptionType;
  strike: number;
  expiry: string;
  expirationDate?: string;
  underlyingRootSymbol?: string;
  quantity: number;
  side: 'BUY' | 'SELL';
  price: number;
  orderType?: OptionOrderType;
  symbol?: string;
  segmentMic?: string;
  exchangeSymbol?: string;
  rootSymbol?: string;
  contractYYYYMM?: string;
  secType?: SecurityType;
  strikePrice?: number;
  callPut?: CallPut;
  strategy?: string;
  delta?: number;
  gamma?: number;
  mlsLegPrice?: string;
};

export type UnderlyingRoot = {
  segmentMic: string;
  underlyingRootSymbol: string;
};

export type OptionExpiration = {
  decimalPlaces: number;
  expirationDate: string;
  rootSymbol: string;
  segmentMic: string;
  underlyingRootSymbol: string;
  underlyingSymbol: string;
  underlyingExchangeSymbol: string;
  underlyingContractYYYYMM: string;
};

export type OptionStrike = {
  decimalPlaces: number;
  rootSymbol: string;
  underlyingSymbol: string;
  underlyingRootSymbol: string;
  segmentMic: string;
  expirationDate: string;
  contractYYYYMM: string;
  symbol: string;
  exchangeSymbol: string;
  strikePrice: number;
  tickSize: number;
  contractSize: number;
  expiry: string;
  callPut: CallPut;
};

export enum OasAction {
  Error = 'ERROR',

  OptionAnalyticsSubscribe = 'OptionAnalytics/Subscribe',
  OptionAnalyticsSubscribeAck = 'OptionAnalytics/Subscribe/Ack',
  OptionAnalyticsUnsubscribe = 'OptionAnalytics/Unsubscribe',
  OptionAnalyticsUnsubscribeAck = 'OptionAnalytics/Unsubscribe/Ack',
  OptionAnalyticsData = 'OptionAnalytics/Data',

  StrikeSeriesSubscribe = 'StrikeSeries/Subscribe',
  StrikeSeriesSubscribeAck = 'StrikeSeries/Subscribe/Ack',
  StrikeSeriesUnsubscribe = 'StrikeSeries/Unsubscribe',
  StrikeSeriesUnsubscribeAck = 'StrikeSeries/Unsubscribe/Ack',
  StrikeSeriesData = 'StrikeSeries/Data',

  VolCurveSubscribe = 'VolCurve/Subscribe',
  VolCurveSubscribeAck = 'VolCurve/Subscribe/Ack',
  VolCurveUnsubscribe = 'VolCurve/Unsubscribe',
  VolCurveUnsubscribeAck = 'VolCurve/Unsubscribe/Ack',
  VolCurveData = 'VolCurve/Data',
}

export type OasStrikeSeriesPayload = {
  segmentMic: string;
  optionRootSymbol: string;
  expirationDate: string; // 'YYYYMMDD'
};

export type OasOptionPayload = OasStrikeSeriesPayload & {
  qepSymbol: string;
};

export type OptionAnalyticsDetails = {
  adjustedBidPrice: number;
  adjustedAskPrice: number;
  isInOptionsBidAskSpreadFit: boolean;
  impliedVolAdjustedMid: number;
};

export type RecentAndHistoricalStats = {
  recentAvg: number;
  recentStdev: number;
  histAvg?: number;
  histStdev?: number;
};

export type Level1Quote = {
  price: number;
  size: number;
  timeUtc?: string;
  exchangeTimeUtc?: string;
};

export type Level1Snapshot = {
  bidQuote: Level1Quote;
  askQuote: Level1Quote;
};

export type OptionAnalytics = {
  segmentMic: string;
  qepSymbol: string;
  analyticsTimeUtc?: string;
  details?: OptionAnalyticsDetails;
  asOfLevel1Snapshot?: Level1Snapshot;
  bidAskSpreadStats?: RecentAndHistoricalStats;
  bidSizeStats?: RecentAndHistoricalStats;
  askSizeStats?: RecentAndHistoricalStats;
  impliedVolBid?: number;
  impliedVolAsk?: number;
  impliedVolMid?: number;
  fittedBidPrice?: number;
  fittedAskPrice?: number;
  fittedBidAskSpread?: number;
  fittedVol: number;
  fittedPrice: number;
  delta: number;
  gamma: number;
  vega: number;
  theta: number;
  modifiedDelta?: number;
  gammaOneStdev?: number;
  gammaOnePercent?: number;
  fittedVolStats?: RecentAndHistoricalStats;
  fitQuality?: number; // @Todo - can only be zero?
};

export type StrikeSeriesKey = {
  optionRoot: string;
  expirationDateLocal: string;
};

export type StrikeSeriesAnalyticsDetails = {
  idxAtmStrikeLower: number;
  idxAtmStrikeUpper: number;
  lastYieldAdjustment: number;
  lastAtmVolCall: number;
  lastAtmVolPut: number;
};

export type StrikeSeriesFitQuality = {
  type: number; // @Todo - can only be zero?
  rmse: number;
  rmseZscore: number;
};

export type StrikeSeries = {
  strikeSeriesKey?: StrikeSeriesKey;
  segmentMic: string;
  underlyingQepSymbol: string;
  analyticsTimeUtc?: string;
  details?: StrikeSeriesAnalyticsDetails;
  forwardPrice: number;
  timeToExpiration: number;
  interestRate: number;
  yieldForOptionPricing: number;
  yieldAdjustment: number;
  forwardMultiplierToMatchStrikes: number;
  optionMultiplier: number;
  atmVolCall: number;
  atmVolPut: number;
  fittedAtmVol: number;
  fitQuality?: StrikeSeriesFitQuality;
};

export type VolCurve = {
  strikeSeriesKey?: StrikeSeriesKey;
  segmentMic: string;
  underlyingQepSymbol: string;
  analyticsTimeUtc?: string;
  forwardPriceToMatchStrikes: number;
  strikePrice: number[];
  moneyness: number[];
  impliedVolBid: number[];
  impliedVolAsk: number[];
  fittedVol: number[];
};

export type OasMessage<T extends OasAction = OasAction, U = unknown> = {
  type: T;
  meta?: {requestId?: string};
  error?: Error | undefined;
  payload: U;
};

export type StrikeSeriesDataMessage = OasMessage<
  OasAction.StrikeSeriesData,
  StrikeSeries
>;

export type OptionAnalyticsDataMessage = OasMessage<
  OasAction.OptionAnalyticsData,
  OptionAnalytics
>;

export type VolCurveDataMessage = OasMessage<
  OasAction.VolCurveData,
  VolCurve
>;
